import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "../../constants/siteData";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 ${
          scrolled
            ? "bg-[#0a192f]/90 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
        style={{ transitionProperty: "background-color, border-color", transitionDuration: "300ms" }}
        data-testid="site-navbar"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a
            href="#home"
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <img src={LOGO_URL} alt="Avado Global Trading" className="h-10 w-auto" />
            <span className="hidden xl:inline font-display text-lg text-cream tracking-tight whitespace-nowrap">
              AVADO GLOBAL TRADING
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8 ml-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm text-white/70 hover:text-coral font-medium tracking-wide relative group"
                style={{ transitionProperty: "color", transitionDuration: "200ms" }}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-coral group-hover:w-full"
                  style={{ transitionProperty: "width", transitionDuration: "300ms" }}
                />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              className="hidden md:inline-flex btn-coral text-sm"
              data-testid="navbar-contact-cta"
            >
              Get In Touch <ArrowUpRight size={16} />
            </a>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 text-cream"
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0a192f] flex flex-col"
            data-testid="mobile-menu-panel"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/5">
              <img src={LOGO_URL} alt="Avado" className="h-10" />
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-cream"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className="font-display text-4xl text-cream hover:text-coral py-2"
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </motion.a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="btn-coral text-sm mt-8 self-start"
                data-testid="mobile-cta"
              >
                Get In Touch <ArrowUpRight size={16} />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
